<script setup>
import { ref, inject } from 'vue';
import AiUsageReceipt from 'dashboard/components-next/ai/AiUsageReceipt.vue';
import CopilotEditor from 'dashboard/components/widgets/WootWriter/CopilotEditor.vue';
import CaptainLoader from 'dashboard/components/widgets/conversation/copilot/CaptainLoader.vue';

const props = defineProps({
  usage: { type: Object, default: null },
  showCopilotEditor: {
    type: Boolean,
    default: false,
  },
  isGeneratingContent: {
    type: Boolean,
    default: false,
  },
  generatedContent: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits([
  'focus',
  'blur',
  'clearSelection',
  'contentReady',
  'send',
]);

const requestEditorHeight = inject('requestEditorHeight', () => {});

const copilotEditorContent = ref('');

// The loader needs no room of its own, the suggestion asks for its own
const onStateEnter = () => {
  if (props.isGeneratingContent) requestEditorHeight(0);
};

const onFocus = () => {
  emit('focus');
};

const onBlur = () => {
  emit('blur');
};

const clearEditorSelection = () => {
  emit('clearSelection');
};

const onSend = () => {
  emit('send', copilotEditorContent.value);
  copilotEditorContent.value = '';
};
</script>

<template>
  <div class="relative">
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="absolute inset-x-0 top-0 pointer-events-none transition-opacity duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
      @enter="onStateEnter"
      @after-enter="emit('contentReady')"
    >
      <CopilotEditor
        v-if="showCopilotEditor && !isGeneratingContent"
        key="copilot-editor"
        v-model="copilotEditorContent"
        class="copilot-editor"
        :generated-content="generatedContent"
        :usage="usage"
        :placeholder="placeholder"
        :min-height="4"
        :enabled-menu-options="[]"
        @focus="onFocus"
        @blur="onBlur"
        @clear-selection="clearEditorSelection"
        @send="onSend"
      />
      <div
        v-else-if="isGeneratingContent"
        key="loading-state"
        class="my-2 rounded-xl border border-n-iris-6 bg-n-solid-2 p-4"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="flex items-center gap-3">
          <div
            class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-iris-3 text-n-iris-11"
          >
            <CaptainLoader class="size-5" />
          </div>
          <div class="min-w-0">
            <p class="m-0 text-sm font-semibold text-n-slate-12">
              {{ $t('CONVERSATION.REPLYBOX.COPILOT_THINKING') }}
            </p>
            <p class="m-0 mt-1 text-xs leading-5 text-n-slate-11">
              {{ $t('CONVERSATION.REPLYBOX.COPILOT_PROCESSING_HINT') }}
            </p>
          </div>
        </div>
        <div
          aria-hidden="true"
          class="mt-4 grid gap-2 motion-safe:animate-pulse"
        >
          <span class="h-2 w-3/4 rounded-full bg-n-alpha-2" />
          <span class="h-2 w-1/2 rounded-full bg-n-alpha-2" />
        </div>
      </div>
    </Transition>
    <AiUsageReceipt
      v-if="usage && !isGeneratingContent && !showCopilotEditor"
      :usage="usage"
      class="mt-2"
    />
  </div>
</template>

<style lang="scss">
.copilot-editor {
  .ProseMirror-menubar {
    display: none;
  }
}
</style>
